import { IntegrationId } from 'integrations/common/streams/tabularMappings/types';
import { UNIFIED_INTEGRATION_IDS } from 'integrations/unified/constants';
import {
  Connection,
  ConnectionRequestPayload,
  UnifiedConnection,
  UnifiedConnectionAuth,
  UnifiedAuthMethod,
} from '@ardoq/api-types/integrations';
import { exhaustiveCheck } from '@ardoq/common-helpers';

const INTEGRATION_IDS_WITH_CONNECTIONS: Array<IntegrationId> = [
  'aws-v3',
  'azure-v3',
  'servicenow-v3',
  'signavio-exporter',
  'it-pedia',
  'celonis-exporter',
  ...UNIFIED_INTEGRATION_IDS,
];
export const isIntegrationWithConnections = (integrationId: IntegrationId) =>
  INTEGRATION_IDS_WITH_CONNECTIONS.includes(integrationId);

export const getConnectionMethod = (
  connection: ConnectionRequestPayload | Connection
) => {
  if ('authenticationType' in connection) {
    return connection.authenticationType;
  }

  if ('authMethod' in connection) {
    return connection.authMethod;
  }

  return 'default';
};

export const prepareUnifiedConnectionAuth = (
  connection: UnifiedConnection
): UnifiedConnectionAuth => {
  if (connection.authMethod === UnifiedAuthMethod.NONE) {
    return {
      authMethod: UnifiedAuthMethod.NONE,
    };
  }
  if (connection.authMethod === UnifiedAuthMethod.BASIC) {
    return {
      authMethod: UnifiedAuthMethod.BASIC,
      accessKey: connection.accessKey,
      secretKey: connection.secretKey,
    };
  }
  if (connection.authMethod === UnifiedAuthMethod.TOKEN) {
    return {
      authMethod: UnifiedAuthMethod.TOKEN,
      token: connection.token,
    };
  }
  if (connection.authMethod === UnifiedAuthMethod.OAUTH_CLIENT_CREDENTIALS) {
    return {
      authMethod: UnifiedAuthMethod.OAUTH_CLIENT_CREDENTIALS,
      clientId: connection.clientId,
      clientSecret: connection.clientSecret,
      authUrl: connection.authUrl,
      scope: connection.scope,
    };
  }
  if (connection.authMethod === UnifiedAuthMethod.CUSTOM_HEADER) {
    return {
      authMethod: UnifiedAuthMethod.CUSTOM_HEADER,
      headerName: connection.headerName,
      headerValue: connection.headerValue,
    };
  }
  if (connection.authMethod === UnifiedAuthMethod.CUSTOM_HEADERS) {
    return {
      authMethod: UnifiedAuthMethod.CUSTOM_HEADERS,
      headers: connection.headers,
    };
  }

  return exhaustiveCheck(connection);
};

export const createEmptyUnifiedConnection = (
  authMethod: Exclude<UnifiedAuthMethod, 'role'>
): UnifiedConnection => {
  const baseConnection = {
    _id: '',
    name: '',
  };

  if (authMethod === UnifiedAuthMethod.NONE) {
    return {
      ...baseConnection,
      authMethod: UnifiedAuthMethod.NONE,
    };
  }
  if (authMethod === UnifiedAuthMethod.BASIC) {
    return {
      ...baseConnection,
      authMethod: UnifiedAuthMethod.BASIC,
      accessKey: '',
      secretKey: '',
    };
  }
  if (authMethod === UnifiedAuthMethod.TOKEN) {
    return {
      ...baseConnection,
      authMethod: UnifiedAuthMethod.TOKEN,
      token: '',
    };
  }
  if (authMethod === UnifiedAuthMethod.OAUTH_CLIENT_CREDENTIALS) {
    return {
      ...baseConnection,
      authMethod: UnifiedAuthMethod.OAUTH_CLIENT_CREDENTIALS,
      clientId: '',
      clientSecret: '',
      authUrl: '',
      scope: '',
    };
  }
  if (authMethod === UnifiedAuthMethod.CUSTOM_HEADER) {
    return {
      ...baseConnection,
      authMethod: UnifiedAuthMethod.CUSTOM_HEADER,
      headerName: '',
      headerValue: '',
    };
  }
  if (authMethod === UnifiedAuthMethod.CUSTOM_HEADERS) {
    return {
      ...baseConnection,
      authMethod: UnifiedAuthMethod.CUSTOM_HEADERS,
      headers: [{ headerName: '', headerValue: '' }],
    };
  }
  return exhaustiveCheck(authMethod);
};
